"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { createRoot, type Root } from "react-dom/client";
import { buildCookieHostCss, COOKIE_CONSENT_HOST_ATTR, syncCookieMountTheme, syncFrameworkStyles } from "./cookie-consent-isolation";
type ShadowApi = {
  shadow: ShadowRoot;
  mount: HTMLDivElement;
  reactMount: HTMLDivElement;
  root: Root | null;
  observer: MutationObserver | null;
};
const shadowApis = new WeakMap<HTMLDivElement, ShadowApi>();
function connectObserver(api: ShadowApi) {
  if (api.observer) {
    return;
  }
  const observer = new MutationObserver(() => {
    syncFrameworkStyles(api.shadow);
    syncCookieMountTheme(api.mount);
  });
  observer.observe(document.documentElement, {
    attributeFilter: ["class", "style"],
    attributes: true
  });
  if (document.body) {
    observer.observe(document.body, {
      attributeFilter: ["class", "style"],
      attributes: true
    });
  }
  observer.observe(document.head, {
    childList: true,
    subtree: true
  });
  api.observer = observer;
}
function ensureShadowApi(host: HTMLDivElement): ShadowApi {
  const existing = shadowApis.get(host);
  if (existing) {
    if (!existing.root) {
      existing.root = createRoot(existing.reactMount);
    }
    connectObserver(existing);
    return existing;
  }
  const shadow = host.attachShadow({
    mode: "closed"
  });
  const mount = document.createElement("div");
  mount.setAttribute("data-cms-cookie-mount", "");
  mount.style.display = "contents";
  const reactMount = document.createElement("div");
  reactMount.setAttribute("data-cms-cookie-react", "");
  reactMount.style.display = "contents";
  mount.appendChild(reactMount);
  shadow.appendChild(mount);
  const api: ShadowApi = {
    shadow,
    mount,
    reactMount,
    root: createRoot(reactMount),
    observer: null
  };
  shadowApis.set(host, api);
  connectObserver(api);
  return api;
}
function releaseShadowRoot(host: HTMLDivElement | null) {
  if (!host) {
    return;
  }
  const api = shadowApis.get(host);
  if (!api) {
    return;
  }
  api.observer?.disconnect();
  api.observer = null;
  api.root?.unmount();
  api.root = null;
}
type CookieConsentShadowHostProps = {
  previewMode: boolean;
  render: () => ReactNode;
};

/**
 * Renders the consent UI in a closed shadow tree with its own React root.
 * React listens on that root, so clicks work. The preferences dialog portals
 * into a node inside that root, so it stays in the shadow tree.
 */
export function CookieConsentShadowHost(t0) {
  const $ = _c(7);
  const {
    previewMode,
    render
  } = t0;
  const hostRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== previewMode || $[1] !== render) {
    t1 = () => {
      const host = hostRef.current;
      if (!host) {
        return;
      }
      host.style.cssText = buildCookieHostCss(previewMode);
      const api = ensureShadowApi(host);
      syncFrameworkStyles(api.shadow);
      syncCookieMountTheme(api.mount);
      api.root?.render(render());
    };
    t2 = [previewMode, render];
    $[0] = previewMode;
    $[1] = render;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      releaseShadowRoot(hostRef.current);
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div ref={hostRef} {...{
      [COOKIE_CONSENT_HOST_ATTR]: ""
    }} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}